import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { pipe, pathOr, filter, propEq, allPass, isEmpty } from 'ramda'
import { select } from '@/store'
import { colors, fontWeights } from '@/styles'
import { createModal, useModalize } from '@/components/modalize/mui'
import Tooltip from '@/components/popper-tooltip'
import Status from './status'
import CloseButton from '../close-button.svg'
import QuestionCircle from '../question-circle.svg'
import DraftIcon from '../document-status-icons/draft-icon.svg?react'
import CreatedIcon from '../document-status-icons/created-icon.svg?react'
import SignedIcon from '../document-status-icons/signed-icon.svg?react'
import PendingIcon from '../document-status-icons/pending-icon.svg?react'

const UpdateNotificationModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 448px;
  margin: auto;
  box-sizing: border-box;
  border-radius: 16px;

  > div {
    padding: 24px;
  }
  > *:first-child {
    border-bottom: 1px solid #00000026;
  }
`

const UpdateNotificationCloseImg = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 10px;
`

const TitleWrapper = styled.div`
  display: block;
  width: 100%;
`

const TitleWrapperInner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const UpdateNotificationModalTitle = styled.div`
  font-family: 'Inter';
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: ${colors.black};
`

const FileTitle = styled.h3`
  margin: 0;
  margin-top: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  max-width: 300px;
  overflow-wrap: break-word;
  color: ${colors.grey56};
`

const UpdateNotificationRequiredWrapper = styled.div`
  width: 100%;
`

const UpdateNotificationRequiredTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`

const UpdateNotificationRequiredContent = styled.div`
  border-left: 1px solid #00000026;
  padding-left: 15px;
`

const UpdateNotificationRequiredContentInner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const UpdateNotificationRequiredContentTitle = styled.div``

const StatusDiv = styled.div``

const UpdateNotificationRequiredPrimaryTitle = styled.p`
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 0;
`

const UpdateNotificationRequiredPrimaryEmail = styled.p`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey56};
  margin: 2px 0;
`
const OtherRecipientsWrapper = styled.div`
  width: 100%;
`

const OtherRecipientsWrapperInner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  img {
    margin-left: 8px;
  }
`

const OtherRecipientsWrapperdTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`

const OtherRecipientsContent = styled.div`
  border-left: 1px solid #00000026;
  padding-left: 15px;
`

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const ItemWrapperContentWrapper = styled.div``

const ItemWrapperContentWrapperName = styled.p`
  margin-bottom: 0;
`

const ItemWrapperContentWrapperEmail = styled.p`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey56};
  margin: 2px; 0;
`

const TooltipWrapper = styled.div`
  p {
    max-width: 220px;
    margin: 0;
    padding: 0;
  }
`

const FullText = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
`

const STATUS = {
  draft: (
    <Status
      text="Draft"
      backgroundColor="#FFFBD8"
      color="#8C7317"
      icon={<DraftIcon fill="#8C7317" />}
    />
  ),
  created: (
    <Status
      text="Created"
      backgroundColor={colors.blue96}
      color={colors.blue40}
      icon={<CreatedIcon fill={colors.blue40} />}
    />
  ),
  sent: (
    <Status
      text="Pending"
      backgroundColor={colors.blue88}
      color={colors.blue24}
      icon={<PendingIcon fill={colors.blue24} />}
    />
  ),
  signed: (
    <Status
      text="Signed"
      backgroundColor={colors.green96}
      color={colors.green24}
      icon={<SignedIcon fill={colors.green24} />}
    />
  ),
  pending: (
    <Status
      text="Pending"
      backgroundColor={colors.blue88}
      color={colors.blue24}
      icon={<PendingIcon fill={colors.blue24} />}
    />
  ),
  not_required: (
    <Status
      text="Pending"
      backgroundColor={colors.blue88}
      color={colors.blue24}
      icon={<PendingIcon fill={colors.blue24} />}
    />
  ),
  unspecified: <Status text="Unspecified" backgroundColor={colors.grey96} color={colors.grey56} />
}

const UpdateNotificationModal = ({ contract, packageFile, dispatch }) => {
  const { t } = useTranslation()
  const packageInfo = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId: contract.id })
  )
  const { close } = useModalize()
  const { file_original_name, state_machine } = packageFile
  const nonRequiredSignersStatus = state_machine === 'sent' ? 'pending' : 'signed'
  const requiredSigners =
    pipe(
      pathOr([], ['signers', packageFile.id]),
      filter(allPass([propEq('recipient_signing_status', 'signature_required')]))
    )(packageInfo) ?? {}

  const nonRequiredSigners =
    pipe(
      pathOr([], ['signers', packageFile.id]),
      filter(allPass([propEq('recipient_signing_status', 'email_notification_required')]))
    )(packageInfo) ?? {}

  return (
    <UpdateNotificationModalWrapper>
      <TitleWrapper>
        <TitleWrapperInner>
          <UpdateNotificationModalTitle>
            {t('l.contract_manager.documents.recipients_modal.title', 'Recipients')}
          </UpdateNotificationModalTitle>
          <UpdateNotificationCloseImg src={CloseButton} onClick={close} alt="" />
        </TitleWrapperInner>

        <FileTitle>{file_original_name}</FileTitle>
      </TitleWrapper>
      {!isEmpty(requiredSigners) && (
        <UpdateNotificationRequiredWrapper>
          <UpdateNotificationRequiredTitle>
            {t('l.contract_manager.documents.recipients_modal.sub_title', 'Required Signatures')}
          </UpdateNotificationRequiredTitle>
          <UpdateNotificationRequiredContent>
            {requiredSigners.map(tenant => (
              <UpdateNotificationRequiredContentInner key={tenant.email}>
                <UpdateNotificationRequiredContentTitle>
                  <UpdateNotificationRequiredPrimaryTitle>
                    {tenant.first_name} {tenant.last_name}
                  </UpdateNotificationRequiredPrimaryTitle>
                  <UpdateNotificationRequiredPrimaryEmail>
                    {tenant.email}
                  </UpdateNotificationRequiredPrimaryEmail>
                </UpdateNotificationRequiredContentTitle>
                <StatusDiv>{STATUS[tenant.state_machine]}</StatusDiv>
              </UpdateNotificationRequiredContentInner>
            ))}
          </UpdateNotificationRequiredContent>
        </UpdateNotificationRequiredWrapper>
      )}
      {!isEmpty(nonRequiredSigners) && (
        <OtherRecipientsWrapper>
          <OtherRecipientsWrapperInner>
            <OtherRecipientsWrapperdTitle>
              {t('l.contract_manager.documents.recipients_modal.other_title', 'Other Recipients')}
            </OtherRecipientsWrapperdTitle>
            <Tooltip
              enabled={true}
              size="medium"
              content={
                <TooltipWrapper>
                  <FullText>
                    {t(
                      'l.contract_manager.documents.tooltip.text',
                      'These recipients will be sent a copy of the contract when all required signatures are completed.'
                    )}
                  </FullText>
                </TooltipWrapper>
              }
              placement={'top'}
            >
              <img src={QuestionCircle} alt="" />
            </Tooltip>
          </OtherRecipientsWrapperInner>
          <OtherRecipientsContent>
            {nonRequiredSigners.map(item => (
              <ItemWrapper key={item.id}>
                <ItemWrapperContentWrapper>
                  <ItemWrapperContentWrapperName>
                    {item.first_name} {item.last_name}
                  </ItemWrapperContentWrapperName>
                  <ItemWrapperContentWrapperEmail>{item.email}</ItemWrapperContentWrapperEmail>
                </ItemWrapperContentWrapper>
                {STATUS[nonRequiredSignersStatus]}
              </ItemWrapper>
            ))}
          </OtherRecipientsContent>
        </OtherRecipientsWrapper>
      )}
    </UpdateNotificationModalWrapper>
  )
}

export const showUpdateNotificationModal = createModal(UpdateNotificationModal, {
  showClose: false
})
